import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bell, CheckCheck, RefreshCw, ArrowRight, ChevronDown, Megaphone, LayoutList, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Header, routeForNotif } from '../components/Header';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { PushToggle } from '../components/PushToggle';
import { useApp } from '../contexts/AppContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const notifIcons = {
  new_message: '💬', new_customer: '👤', sale_complete: '💰',
  ai_response: '✨', campaign_update: '📢', system: '🔔',
  ticket: '🎫', ticket_created: '🎫', escalation: '🚨', automation: '⚡',
  noah_deal_closed: '💰', weekly_ranking: '🏆',
};

const typeLabels = {
  new_message: 'Mensajes', ai_response: 'Noah IA', new_customer: 'Clientes',
  sale_complete: 'Ventas', noah_deal_closed: 'Ventas', campaign_update: 'Campañas',
  system: 'Sistema', ticket: 'Tickets', ticket_created: 'Tickets',
  escalation: 'Escalaciones', automation: 'Automatizaciones', weekly_ranking: 'Reportes',
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const diff = (new Date() - d) / 1000 / 60;
  if (diff < 1) return 'Ahora';
  if (diff < 60) return `Hace ${Math.floor(diff)} min`;
  if (diff < 1440) return `Hace ${Math.floor(diff / 60)} h`;
  return d.toLocaleString('es');
};

const NotifRow = ({ n, onOpen }) => (
  <Card data-testid={`notif-item-${n.id}`} onClick={() => onOpen(n)}
    className={cn("p-3 sm:p-4 cursor-pointer transition-colors border",
      n.read ? "hover:bg-muted/30" : "bg-primary/5 border-primary/30 hover:bg-primary/10")}>
    <div className="flex items-start gap-3">
      <span className="text-xl shrink-0">{notifIcons[n.type] || '🔔'}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={cn("text-sm font-semibold", !n.read && "text-primary")}>{n.title}</p>
          {!n.read && <Badge className="bg-destructive text-destructive-foreground text-[9px] h-4 px-1.5">Nuevo</Badge>}
          {n.type && <Badge variant="outline" className="text-[9px] h-4 px-1.5">{typeLabels[n.type] || n.type}</Badge>}
        </div>
        <p className="text-xs sm:text-sm text-muted-foreground mt-1 break-words">{n.message}</p>
        <p className="text-[11px] text-muted-foreground mt-1.5">
          {formatTime(n.created_at)} · <span className="text-primary inline-flex items-center gap-1">
            Abrir {routeForNotif(n).replace('/', '') || 'dashboard'} <ArrowRight className="w-3 h-3" />
          </span>
        </p>
      </div>
    </div>
  </Card>
);

export default function Notifications() {
  const { notifications, campaigns, fetchNotifications, markNotificationRead, markAllNotificationsRead, chats, setSelectedChat } = useApp();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [grouped, setGrouped] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const types = useMemo(() => Array.from(new Set(notifications.map(n => n.type).filter(Boolean))), [notifications]);

  const filtered = useMemo(() => {
    if (filter === 'all') return notifications;
    if (filter === 'unread') return notifications.filter(n => !n.read);
    return notifications.filter(n => n.type === filter);
  }, [notifications, filter]);

  // Agrupación por campaña
  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach(n => {
      const key = n.campaign_id || 'none';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(n);
    });
    return Array.from(map.entries())
      .map(([key, items]) => {
        const campaign = campaigns.find(c => c.id === key);
        return {
          key,
          name: campaign?.name || (key === 'none' ? 'Sin campaña' : 'Campaña eliminada'),
          color: campaign?.color || '#559975',
          items,
          unread: items.filter(n => !n.read).length,
        };
      })
      .sort((a, b) => b.unread - a.unread || b.items.length - a.items.length);
  }, [filtered, campaigns]);

  const refresh = async () => {
    setRefreshing(true);
    await fetchNotifications();
    setRefreshing(false);
  };

  const readGroup = async (groupKey, groupName) => {
    try {
      await axios.put(`${API}/notifications/read-all?campaign_id=${encodeURIComponent(groupKey)}`);
      await fetchNotifications();
      toast.success(`Avisos de ${groupName} marcados como leídos`);
    } catch { toast.error('No pude marcar ese grupo'); }
  };

  const open = (notif) => {
    if (!notif.read) markNotificationRead(notif.id);
    if (notif.chat_id) {
      const chat = chats.find(c => c.id === notif.chat_id);
      if (chat) setSelectedChat(chat);
    }
    navigate(routeForNotif(notif));
  };

  const chip = (value, label, count) => (
    <button key={value} data-testid={`notif-filter-${value}`} onClick={() => setFilter(value)}
      className={cn("px-3 py-1.5 rounded-full text-xs font-medium border transition-colors whitespace-nowrap",
        filter === value ? "bg-primary text-primary-foreground border-primary" : "bg-muted/40 border-border hover:bg-muted")}>
      {label}{count !== undefined ? ` (${count})` : ''}
    </button>
  );

  return (
    <div data-testid="notifications-page">
      <Header title="Centro de Alertas" subtitle={`${notifications.length} avisos · ${unreadCount} sin leer`} />

      <div className="p-3 sm:p-6 space-y-4 pb-28 max-w-3xl">
        <Card className="p-4" data-testid="push-card">
          <PushToggle />
        </Card>

        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="notif-refresh" variant="outline" size="sm" className="h-9" onClick={refresh} disabled={refreshing}>
            <RefreshCw className={cn("w-4 h-4 sm:mr-1.5", refreshing && "animate-spin")} />
            <span className="hidden sm:inline">Actualizar</span>
          </Button>
          <Button data-testid="notif-mark-all" variant="outline" size="sm" className="h-9" onClick={markAllNotificationsRead} disabled={unreadCount === 0}>
            <CheckCheck className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Marcar todas leídas</span>
            <span className="sm:hidden">Leer todo</span>
          </Button>
          <div className="flex items-center rounded-full border border-border overflow-hidden ml-auto">
            <button data-testid="notif-view-grouped" onClick={() => setGrouped(true)}
              className={cn("px-3 h-9 text-xs font-medium flex items-center gap-1.5", grouped ? "bg-primary text-primary-foreground" : "hover:bg-muted")}>
              <Layers className="w-3.5 h-3.5" /> Por campaña
            </button>
            <button data-testid="notif-view-list" onClick={() => setGrouped(false)}
              className={cn("px-3 h-9 text-xs font-medium flex items-center gap-1.5", !grouped ? "bg-primary text-primary-foreground" : "hover:bg-muted")}>
              <LayoutList className="w-3.5 h-3.5" /> Cronológico
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap pb-1" data-testid="notif-filter-row">
          {chip('all', 'Todas', notifications.length)}
          {chip('unread', 'Sin leer', unreadCount)}
          {types.map(tp => chip(tp, typeLabels[tp] || tp, notifications.filter(n => n.type === tp).length))}
        </div>

        {grouped ? (
          <div className="space-y-3" data-testid="notif-groups">
            {groups.map(g => (
              <div key={g.key} data-testid={`notif-group-${g.key}`} className="rounded-2xl border border-border overflow-hidden">
                <div className="flex items-center gap-2 p-3 bg-muted/30">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
                  <button data-testid={`notif-group-toggle-${g.key}`}
                    onClick={() => setCollapsed(prev => ({ ...prev, [g.key]: !prev[g.key] }))}
                    className="flex items-center gap-2 min-w-0 flex-1 text-left">
                    <Megaphone className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="text-sm font-semibold truncate">{g.name}</span>
                    <Badge variant="outline" className="text-[10px] shrink-0">{g.items.length}</Badge>
                    {g.unread > 0 && <Badge className="bg-destructive text-destructive-foreground text-[10px] shrink-0">{g.unread} sin leer</Badge>}
                    <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform shrink-0 ml-auto", collapsed[g.key] && "-rotate-90")} />
                  </button>
                  {g.unread > 0 && (
                    <Button data-testid={`notif-group-read-${g.key}`} variant="ghost" size="sm" className="h-8 text-xs shrink-0"
                      onClick={() => readGroup(g.key, g.name)}>
                      <CheckCheck className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                {!collapsed[g.key] && (
                  <div className="p-2 space-y-2">
                    {g.items.map(n => <NotifRow key={n.id || n.created_at} n={n} onOpen={open} />)}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(n => <NotifRow key={n.id || n.created_at} n={n} onOpen={open} />)}
          </div>
        )}

        {filtered.length === 0 && (
          <div className="text-center py-16 text-muted-foreground" data-testid="notif-empty">
            <Bell className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No hay notificaciones en este filtro</p>
          </div>
        )}
      </div>
    </div>
  );
}
